import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang, IMAGES } from "../i18n";
import { Volume2, VolumeX, ScanLine, ShieldCheck, CalendarCheck, Check, BadgeCheck, Sprout } from "lucide-react";

const STEP_ICONS = [ScanLine, ShieldCheck, CalendarCheck, BadgeCheck, Sprout];

export const VideoBand = () => {
  const { t } = useLang();
  const v = t.video;
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const vid = ref.current;
    if (!vid) return;
    const onTime = () => {
      const tt = vid.currentTime;
      const next = tt < 3.5 ? 0 : tt < 7 ? 1 : tt < 10 ? 2 : tt < 14 ? 3 : 4;
      setStep((prev) => (prev === next ? prev : next));
    };
    vid.addEventListener("timeupdate", onTime);
    return () => vid.removeEventListener("timeupdate", onTime);
  }, []);

  const toggleMute = () => {
    if (ref.current) {
      ref.current.muted = !ref.current.muted;
      setMuted(ref.current.muted);
    }
  };

  const StepIcon = STEP_ICONS[step] || BadgeCheck;

  return (
    <section id="film" data-testid="video-section" className="relative pt-28 md:pt-32 pb-14 md:pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-[2rem] overflow-hidden border border-[var(--border)] shadow-[0_40px_100px_-40px_rgba(30,43,35,0.4)]"
        >
          <video
            ref={ref}
            data-testid="hero-video"
            className="w-full h-[56vw] max-h-[680px] min-h-[360px] object-cover"
            poster={IMAGES.videoPoster}
            autoPlay
            loop
            muted
            playsInline
          >
            <source src="/hero.webm?v=7" type="video/webm" />
            <source src="/hero.mp4?v=7" type="video/mp4" />
          </video>

          {/* readability gradients */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/25 pointer-events-none" />

          {/* mute toggle */}
          <button
            onClick={toggleMute}
            data-testid="video-mute-toggle"
            className="absolute top-6 right-6 w-11 h-11 rounded-full glass flex items-center justify-center hover:scale-105 transition-transform"
            aria-label="toggle sound"
          >
            {muted ? <VolumeX className="w-4 h-4 text-[var(--text-primary)]" /> : <Volume2 className="w-4 h-4 text-[var(--gold)]" />}
          </button>

          {/* readable step tracker */}
          <div className="absolute top-6 left-1/2 -translate-x-1/2 hidden sm:flex items-center gap-2">
            {v.steps.map((label, i) => (
              <div
                key={i}
                data-testid={`video-step-${i}`}
                className={`flex items-center gap-2 rounded-full px-3 py-1.5 transition-all duration-500 ${i === step ? "bg-[var(--gold)] text-[#12241b]" : "glass text-[var(--text-secondary)]"}`}
              >
                <span className="font-jetbrains text-[10px]">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-outfit text-[11px] font-medium whitespace-nowrap">{label}</span>
              </div>
            ))}
          </div>

          {/* readable animated app callout */}
          <div className="absolute bottom-6 right-6 w-[260px] hidden sm:block">
            <div className="glass rounded-2xl p-4 shadow-xl" data-testid="video-callout">
              <div className="flex items-center gap-2 mb-3">
                <StepIcon className="w-4 h-4 text-[var(--gold)]" />
                <span className="overline text-[9px] text-[var(--text-primary)]">{v.steps[step]}</span>
                <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[var(--green)] animate-pulse" />
              </div>
              <AnimatePresence mode="wait">
                {step < 2 ? (
                  <motion.div key="scan" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                    <div className="h-1.5 rounded-full bg-black/10 overflow-hidden">
                      <motion.div className="h-full bg-[var(--green)]" initial={{ width: "0%" }} animate={{ width: "100%" }} transition={{ duration: 2.6 }} />
                    </div>
                    <div className="font-jetbrains text-[10px] text-[var(--text-secondary)] mt-2">ID: A1B2C3D4E5F6</div>
                  </motion.div>
                ) : step === 2 ? (
                  <motion.div key="exp" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    <div className="flex items-center gap-2">
                      <span className="font-jetbrains text-sm text-[var(--text-secondary)] line-through">{v.expOld}</span>
                      <span className="font-cormorant text-2xl text-[var(--text-primary)]">→ {v.expNew}</span>
                    </div>
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[var(--green)] text-white px-2.5 py-1">
                      <Check className="w-3 h-3" />
                      <span className="font-outfit text-[10px] font-medium">{v.fresh}</span>
                    </div>
                  </motion.div>
                ) : step === 3 ? (
                  <motion.div key="orig" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    <div className="font-cormorant text-xl text-[var(--text-primary)]">{v.steps[3]}</div>
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[var(--green)] text-white px-2.5 py-1">
                      <Check className="w-3 h-3" />
                      <span className="font-outfit text-[10px] font-medium">{v.verified}</span>
                    </div>
                  </motion.div>
                ) : (
                  <motion.div key="plant" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                    <div className="font-cormorant text-xl text-[var(--text-primary)]">{v.steps[4]}</div>
                    <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[var(--green)] text-white px-2.5 py-1">
                      <Check className="w-3 h-3" />
                      <span className="font-outfit text-[10px] font-medium">{v.plant}</span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* caption */}
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 max-w-[62%]">
            <h2 className="font-cormorant font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-white">
              {v.title}
            </h2>
            <p className="font-outfit font-light text-white/80 mt-3 max-w-xl">{v.sub}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
